import clsx from "clsx";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CurrencyPrimitive, EnumPaymentType } from "@/api/cashier";
import { CopyToClipboard } from "@/components/copy-to-clipboard/CopyToClipboard.tsx";
import { ExchangeRate } from "@/components/ExchangeRate";
import TransactionDetailsLoadingSkeleton from "@/components/transaction/transaction-details/TransactionDetailsLoadingSkeleton";
import { TransactionHistoryTypeLabel } from "@/components/transaction/transaction-history-table/TransactionHistoryTypeLabel";
import { useFormatAmount } from "@/hooks/useFormatAmount.ts";
import { useFormatDate } from "@/hooks/useFormatDate.ts";
import styles from "./TransactionDetails.module.css";

type TransactionDetailsProps = {
  isLoading: boolean;
  transactionType: EnumPaymentType;
  transactionId: string;
  createdAt: Date;
  amount: number;
  transactionCurrency: CurrencyPrimitive;
  amountInProcessingCurrency?: number;
  netAmountInTradingCurrency?: number;
  feeAmount?: number;
  feeCurrency?: CurrencyPrimitive;
  exchangeRate?: number;
  exchangeRateUnit?: number;
  processingCurrency?: CurrencyPrimitive;
  cashAdjustmentReason?: string;
};

export const TransactionDetails = ({
  isLoading,
  transactionType,
  amount,
  transactionCurrency,
  transactionId,
  feeAmount,
  feeCurrency,
  exchangeRate,
  exchangeRateUnit,
  processingCurrency,
  amountInProcessingCurrency,
  netAmountInTradingCurrency,
  createdAt,
  cashAdjustmentReason,
}: TransactionDetailsProps) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const { formatAmount } = useFormatAmount();

  const yourPaymentActionMap: Record<EnumPaymentType, string> = {
    deposit: t("transactions.history.details.your_deposit"),
    withdrawal: t("transactions.history.details.your_withdrawal"),
    transfer: t("transactions.history.details.your_transfer"),
    cashAdjustment: t("transactions.history.details.your_cash_adjustment"),
  };

  const amountDisplay = useMemo(() => {
    // Withdrawal: show amount in transaction currency, with processing currency in parentheses if different
    if (transactionType === "withdrawal") {
      const netTradingAmount = netAmountInTradingCurrency ?? amount;
      const tradingDisplay = formatAmount(
        netTradingAmount,
        transactionCurrency
      );

      if (
        processingCurrency &&
        processingCurrency.code !== transactionCurrency.code &&
        amountInProcessingCurrency != null
      ) {
        const processingDisplay = formatAmount(
          amountInProcessingCurrency,
          processingCurrency
        );
        return `${tradingDisplay} (${processingDisplay})`;
      }

      return tradingDisplay;
    }

    // Deposit: show amount in processing currency
    if (transactionType === "deposit") {
      const currency = processingCurrency ?? transactionCurrency;
      const amountToShow = amountInProcessingCurrency ?? amount;
      return formatAmount(amountToShow, currency);
    }

    // Transfer: handled separately in the UI with "You sent" and "You received" labels
    if (transactionType === "transfer") {
      return formatAmount(amount, transactionCurrency);
    }

    // Cash Adjustment: show amount in transaction currency
    if (transactionType === "cashAdjustment") {
      return formatAmount(amount, transactionCurrency);
    }

    return formatAmount(amount, transactionCurrency);
  }, [
    transactionType,
    amount,
    transactionCurrency,
    amountInProcessingCurrency,
    netAmountInTradingCurrency,
    processingCurrency,
    formatAmount,
  ]);

  const feeDisplay = useMemo(() => {
    if (!feeAmount || transactionType === "cashAdjustment") return null;

    // Fee currency comes from the API response (fee_money.code).
    // Fall back to processing/transaction currency only if fee currency is missing.
    const currencyToUse =
      feeCurrency ?? processingCurrency ?? transactionCurrency;
    return formatAmount(feeAmount, currencyToUse);
  }, [
    feeAmount,
    transactionType,
    feeCurrency,
    processingCurrency,
    transactionCurrency,
    formatAmount,
  ]);

  const exchangeRateDisplay = useMemo(() => {
    if (
      !exchangeRateUnit ||
      !processingCurrency ||
      !exchangeRate ||
      !transactionCurrency
    ) {
      return null;
    }

    if (!processingCurrency || !transactionCurrency) {
      return null;
    }

    if (processingCurrency.code === transactionCurrency.code) {
      return null;
    }

    return (
      <ExchangeRate
        unit={exchangeRateUnit ?? 1}
        currency={processingCurrency}
        exchangeRate={exchangeRate}
        exchangeCurrency={transactionCurrency}
      />
    );
  }, [exchangeRateUnit, processingCurrency, exchangeRate, transactionCurrency]);

  const amountInProcessingCurrencyDisplay = useMemo(() => {
    if (!amountInProcessingCurrency) return null;

    const currency = processingCurrency ?? transactionCurrency;
    return formatAmount(amountInProcessingCurrency, currency);
  }, [
    amountInProcessingCurrency,
    processingCurrency,
    transactionCurrency,
    formatAmount,
  ]);

  if (isLoading) {
    return <TransactionDetailsLoadingSkeleton />;
  }

  return (
    <div className={styles.detailWrapper}>
      <h2 className={styles.detailTitle}>
        <TransactionHistoryTypeLabel
          type={transactionType}
          isDetailsTitle={true}
        />
      </h2>
      <div className={styles.detailRow}>
        <p className={styles.detailRowTitle}>
          {yourPaymentActionMap[transactionType] ?? transactionType}
        </p>
        {amountDisplay && (
          <p className={styles.detailRowValue}>{amountDisplay}</p>
        )}
      </div>

      {feeDisplay && (
        <div className={styles.detailRow}>
          <p className={styles.detailRowTitle}>{t("transfer.fee")}</p>
          <p className={styles.detailRowValue}>{feeDisplay}</p>
        </div>
      )}

      {transactionType === "cashAdjustment" && (
        <div className={styles.detailRow}>
          <p className={styles.detailRowTitle}>
            {t("transactions.history.details.cash_adjustment_reason")}
          </p>
          <p className={styles.detailRowValue}>{cashAdjustmentReason}</p>
        </div>
      )}

      {transactionType === "transfer" && (
        <div className={styles.detailRow}>
          <p className={styles.detailRowTitle}>
            {t("transactions.history.details.your_transfer_received")}
          </p>
          <p className={styles.detailRowValue}>
            {amountInProcessingCurrencyDisplay}
          </p>
        </div>
      )}
      {exchangeRateDisplay && (
        <div className={styles.detailRow}>
          <p className={styles.detailRowTitle}>{t("transfer.exchange_rate")}</p>
          <p className={styles.detailRowValue}>{exchangeRateDisplay}</p>
        </div>
      )}
      <div className={clsx(styles.detailRow, styles.withDivider)}>
        <p className={styles.detailRowTitle}>
          {t("transactions.history.table.order_number")}
        </p>
        <div className={styles.valueWithCopy}>
          <p className={styles.detailRowValue}>{transactionId}</p>
          <CopyToClipboard
            label={t("transactions.history.table.order_number")}
            text={transactionId}
          />
        </div>
      </div>
      <div className={styles.detailRow}>
        <p className={styles.detailRowTitle}>
          {t("transactions.history.details.date")}
        </p>
        <p className={styles.detailRowValue}>{formatDate(createdAt)}</p>
      </div>
    </div>
  );
};
